import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";

// Dynamic imports
import Section from "@/Components/LayoutComponents/Section";
import { toWebp } from "@/utils/common";
const TrustCardMain = dynamic(() => import("./TrustCardMain"));

interface TrustedProps {
  data?: any;
}

interface LogoItem {
  image?: any;
  title?: string;
}

const useDeviceColumns = () => {
  const [columns, setColumns] = useState(4); // Default to tablet

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setColumns(2); // Mobile
      } else if (width < 1024) {
        setColumns(3); // Tablet
      } else if (width < 1280) {
        setColumns(4); // Desktop
      } else {
        setColumns(4); // Desktop
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
};

const Trusted: React.FC<TrustedProps> = ({ data }) => {
  const columnCount = useDeviceColumns();
  const logos = data?.logos || data?.slide;

  const items = logos?.map((logo: LogoItem) => ({
    logoSrc: toWebp(logo?.image?.data?.attributes?.url || ""),
    company: logo?.title || "",
  }));

  const splitIntoColumns = <T,>(arr: T[], cols: number): T[][] => {
    const output: T[][] = Array.from({ length: cols }, () => []);
    arr.forEach((item, index) => {
      output[index % cols].push(item);
    });
    return output;
  };

  const columns = splitIntoColumns(items, columnCount);

  const repeatedColumns = columns.map((col) => [...col, ...col]);

  if (!items?.length) return null;

  return (
    <Section>
      <div className="container">
        {data?.title && (
          <h2 className="text-heading-1 font-bold text-primary-text text-center">
            {data?.title}
          </h2>
        )}

        <div
          className="xl:mt-10 mt-8 relative
            after:content-[''] after:absolute after:w-full after:h-[20%] after:z-10 after:top-0 after:right-0 after:bg-[linear-gradient(180deg,_#FFFFFF_15%,_rgba(255,255,255,0)_100%)]
            before:content-[''] before:absolute before:w-full before:h-[20%] before:z-10 before:bottom-0 before:right-0 before:bg-[linear-gradient(360deg,_#FFFFFF_15%,_rgba(255,255,255,0)_100%)]"
        >
          <div className="grid xl:grid-cols-4 sm:grid-cols-3 grid-cols-2 md:gap-[30px] gap-6 lg:max-h-[640px] md:max-h-[583px] max-h-[451px] overflow-hidden">
            {repeatedColumns.map((group, index) => (
              <TrustCardMain
                key={index}
                className={index % 2 === 0 ? "marquee-down" : "marquee-up"}
                classNameMain="flex-1"
                items={group}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Trusted;
